import React, { useState } from 'react';
import { postRequest } from './Services/HttpClient';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import SanskritKeyboard from './SanskritKeyboard';
import './Vibhakti.css';

const GrammaticalCase = {
  prathama: 'prathama',
  dvitiya: 'dvitiya',
  tritiya: 'tritiya',
  chaturthi: 'chaturthi',
  panchami: 'panchami',
  shashthi: 'shashthi',
  saptami: 'saptami'
};

const Vachanam = {
  eka: "eka",
  dvi: "dvi",
  bahu: "bahu"
};

const vibhaktiLabels = {
  prathama: { sanskrit: 'प्रथमा', english: 'Nominative' },
  dvitiya: { sanskrit: 'द्वितीया', english: 'Accusative' },
  tritiya: { sanskrit: 'तृतीया', english: 'Instrumental' },
  chaturthi: { sanskrit: 'चतुर्थी', english: 'Dative' },
  panchami: { sanskrit: 'पञ्चमी', english: 'Ablative' },
  shashthi: { sanskrit: 'षष्ठी', english: 'Genitive' },
  saptami: { sanskrit: 'सप्तमी', english: 'Locative' },
  sambodhana: { sanskrit: 'सम्बोधन', english: 'Vocative' }
};

const Vibhakti = () => {
  const [outputData, setOutputData] = useState(null);
  const [inputData, setInputData] = useState({ inputWord: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showKeyboard, setShowKeyboard] = useState(false);

  const handleChange = async () => {
    if (!inputData.inputWord || !inputData.inputWord.trim()) return;

    setOutputData(null);
    setError(null);
    setLoading(true);

    try {
      const result = await postRequest('vibhaktis', JSON.stringify({ "input": inputData.inputWord }), true);

      // Check if result has expected structure
      if (!result || !result.data || !result.data.rows) {
        console.error('Unexpected API response:', result);
        setError('अप्रत्याशित-प्रतिक्रिया प्राप्ता। (Unexpected response format)');
        return;
      }

      setOutputData(result.data.rows);
    } catch (err) {
      console.error('Vibhakti API error:', err);
      setError('विश्लेषणे त्रुटिः। कृपया पुनः प्रयासं कुर्वन्तु। (Analysis failed. Please try again.)');
    } finally {
      setLoading(false);
    }
  };

  const updateInputStateInputWord = (inputWordEvent) => {
    const inputWord = inputWordEvent.target.value;
    setInputData({ ...inputData, inputWord });
  };

  const handleClear = () => {
    setInputData({ inputWord: '' });
    setOutputData(null);
    setError(null);
  };

  const renderVibhaktiTable = () => {
    if (!outputData) return null;

    const cases = [
      GrammaticalCase.prathama,
      GrammaticalCase.dvitiya,
      GrammaticalCase.tritiya,
      GrammaticalCase.chaturthi,
      GrammaticalCase.panchami,
      GrammaticalCase.shashthi,
      GrammaticalCase.saptami
    ];

    return (
      <table className="vibhakti-table">
        <thead>
          <tr>
            <th className="vibhakti-header-cell">विभक्तिः</th>
            <th className="vibhakti-header-cell">एकवचनम्</th>
            <th className="vibhakti-header-cell">द्विवचनम्</th>
            <th className="vibhakti-header-cell">बहुवचनम्</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((caseKey, index) => {
            const row = outputData[caseKey];
            const label = vibhaktiLabels[caseKey];
            return (
              <tr key={caseKey} className={index % 2 === 0 ? 'vibhakti-row-even' : 'vibhakti-row-odd'}>
                <td className="vibhakti-label-cell">
                  <span className="vibhakti-sanskrit">{label?.sanskrit}</span>
                </td>
                <td className="vibhakti-data-cell">{row?.data?.eka || ''}</td>
                <td className="vibhakti-data-cell">{row?.data?.dvi || ''}</td>
                <td className="vibhakti-data-cell">{row?.data?.bahu || ''}</td>
              </tr>
            );
          })}
          {/* Sambodhana row - use prathama data or show empty */}
          <tr className="vibhakti-row-odd">
            <td className="vibhakti-label-cell">
              <span className="vibhakti-sanskrit">{vibhaktiLabels.sambodhana.sanskrit}</span>
            </td>
            <td className="vibhakti-data-cell">{outputData.prathama?.data?.eka || ''}</td>
            <td className="vibhakti-data-cell">{outputData.prathama?.data?.dvi || ''}</td>
            <td className="vibhakti-data-cell">{outputData.prathama?.data?.bahu || ''}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const handleKeyboardInput = (key) => {
    if (key === 'BACKSPACE') {
      setInputData(prev => ({
        ...prev,
        inputWord: prev.inputWord.slice(0, -1)
      }));
    } else {
      setInputData(prev => ({
        ...prev,
        inputWord: (prev.inputWord || '') + key
      }));
    }
  };

  return (
    <Container fluid="md" className="vibhakti-page">
      {/* Header */}
      <Row>
        <Col>
          <div className="vibhakti-header">
            <h1 className="vibhakti-title">विभक्तिः (Vibhakti)</h1>
            <p className="vibhakti-description">Declension forms of Sanskrit nouns</p>
          </div>
        </Col>
      </Row>

      {/* Main Content Row */}
      <Row className="vibhakti-main-row">
        <Col xs={12} md={5} className="vibhakti-col vibhakti-input-col">
          <div className="vibhakti-section">
            <div className="vibhakti-section-header">
              <span className="section-label">प्रातिपदिकम् (Input)</span>
            </div>
            <textarea
              className="vibhakti-textarea vibhakti-input"
              value={inputData.inputWord || ''}
              disabled={loading}
              onChange={updateInputStateInputWord}
              placeholder="प्रातिपदिकं लिखतु...&#10;(Enter a noun stem)"
            />
          </div>
        </Col>
        <Col xs={12} md={7} className="vibhakti-col vibhakti-output-col">
          <div className="vibhakti-section vibhakti-output-section">
            <div className="vibhakti-section-header">
              <span className="section-label">विभक्तिरूपाणि</span>
            </div>
            <div className="vibhakti-table-container">
              {loading ? (
                <div className="vibhakti-loading">विश्लेषणम्...</div>
              ) : outputData ? (
                renderVibhaktiTable()
              ) : (
                <div className="vibhakti-placeholder">
                  विभक्तिरूपाणि अत्र प्रदर्श्यन्ते...<br />
                  (Declension forms will appear here)
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Actions Row */}
      <Row className="vibhakti-actions-row">
        <Col>
          <div className="vibhakti-actions">
            <Button
              variant="primary"
              onClick={handleChange}
              disabled={loading || !inputData.inputWord || !inputData.inputWord.trim()}
              className="vibhakti-submit-btn"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  विश्लेषणम्...
                </>
              ) : (
                'प्रदर्शय (Show)'
              )}
            </Button>
            <Button
              variant="outline-secondary"
              className="vibhakti-clear-btn"
              onClick={handleClear}
              disabled={!inputData.inputWord && !outputData}
            >
              स्वच्छं कुरु (Clear)
            </Button>
          </div>
        </Col>
      </Row>

      {/* Error Display */}
      {error && (
        <Row className="vibhakti-error-row">
          <Col>
            <div className="vibhakti-error">
              {error}
            </div>
          </Col>
        </Row>
      )}

      {/* Sanskrit Keyboard */}
      <Row className="vibhakti-keyboard-row">
        <Col>
          <SanskritKeyboard
            onKeyPress={handleKeyboardInput}
            isVisible={showKeyboard}
            onToggle={() => setShowKeyboard(!showKeyboard)}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default Vibhakti;
